import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { KnownMapping, ParsedWorksheet } from "@/types";

interface UploadState {
  fileUploaded: boolean;
  mappingComplete: boolean;
  /** The original File object -- needed by MappingPage for POST /process */
  file: File | null;
  fileName: string;
  /** Document ID returned by the upload API -- used as file_id in /process */
  documentId: string;
  schemaId: string;
  schemaName: string;
  worksheets: ParsedWorksheet[];
  inputColumns: string[];
  savedMappings: KnownMapping[];
  dqxRunId: number | null;
  dqxJobId: string;
  dqxTriggerMessage: string;
  // ── Intelligent File Classification state ──
  // `detectedType` is the backend's suggestion from the three-step
  // classification cascade; `reportType` is the value the user has
  // confirmed in the Preview dropdown (seeded from `detectedType`).
  // Kept separate so we can distinguish "auto-detected" from "manually
  // confirmed" for telemetry and for avoiding overwrites once the user
  // has interacted with the dropdown.
  /** Report type the user has confirmed on the Preview screen (e.g. "claims"). */
  reportType: string | null;
  /** Raw detected_type from the classification API (includes "unknown"). */
  detectedType: string | null;
  /** Allowed report_type values for the active schema — drives the dropdown options. */
  allowedReportTypes: string[];
  /** True while POST /files/classify is in flight. Gates the Confirm button. */
  isClassifying: boolean;
}

interface UploadContextValue extends UploadState {
  confirmUpload: (data: {
    file: File;
    fileName: string;
    documentId: string;
    schemaId: string;
    schemaName: string;
    worksheets: ParsedWorksheet[];
    reportType?: string | null;
  }) => void;
  resetUpload: () => void;
  saveMappings: (mappings: KnownMapping[]) => void;
  setDqxRunInfo: (data: { runId: number | null; jobId?: string; message?: string }) => void;
  // Classification helpers — called from the UploadPage when the
  // classify request starts, completes, and when the user picks a value
  // in the Preview dropdown.
  setClassifying: (isClassifying: boolean) => void;
  setClassificationResult: (data: {
    detectedType: string | null;
    allowedReportTypes: string[];
  }) => void;
  setReportType: (reportType: string | null) => void;
}

const initialState: UploadState = {
  fileUploaded: false,
  mappingComplete: false,
  file: null,
  fileName: "",
  documentId: "",
  schemaId: "",
  schemaName: "",
  worksheets: [],
  inputColumns: [],
  savedMappings: [],
  dqxRunId: null,
  dqxJobId: "",
  dqxTriggerMessage: "",
  reportType: null,
  detectedType: null,
  allowedReportTypes: [],
  isClassifying: false,
};

const UploadContext = createContext<UploadContextValue | null>(null);

export function UploadProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UploadState>(initialState);

  const confirmUpload = useCallback(
    (data: {
      file: File;
      fileName: string;
      documentId: string;
      schemaId: string;
      schemaName: string;
      worksheets: ParsedWorksheet[];
      reportType?: string | null;
    }) => {
      const allHeaders = new Set<string>();
      for (const ws of data.worksheets) {
        if (ws.isMetadata) continue;
        for (const h of ws.headers) {
          allHeaders.add(h);
        }
      }

      setState((prev) => ({
        ...prev,
        fileUploaded: true,
        file: data.file,
        fileName: data.fileName,
        documentId: data.documentId,
        schemaId: data.schemaId,
        schemaName: data.schemaName,
        worksheets: data.worksheets,
        inputColumns: Array.from(allHeaders),
        savedMappings: [],
        dqxRunId: null,
        dqxJobId: "",
        dqxTriggerMessage: "",
        // Persist the confirmed reportType so downstream pages
        // (MappingPage → /process) can pass it to the backend. If the
        // caller didn't supply one (legacy flow), fall back to whatever
        // is already in state.
        reportType: data.reportType ?? prev.reportType,
      }));
    },
    []
  );

  const resetUpload = useCallback(() => {
    setState(initialState);
  }, []);

  const saveMappings = useCallback((mappings: KnownMapping[]) => {
    setState((prev) => ({
      ...prev,
      savedMappings: mappings,
      mappingComplete: true,
    }));
  }, []);

  const setDqxRunInfo = useCallback(
    (data: { runId: number | null; jobId?: string; message?: string }) => {
      setState((prev) => ({
        ...prev,
        dqxRunId: data.runId,
        dqxJobId: data.jobId ?? "",
        dqxTriggerMessage: data.message ?? "",
      }));
    },
    []
  );

  // Classification helpers — kept as small targeted setters so the
  // UploadPage can flip the spinner, land the classification result, and
  // update the dropdown selection without a bulky single-setter API.
  //
  // When a new classification run *starts* (isClassifying -> true) we
  // also clear any previous result. Without this, a user who uploaded
  // file A (e.g. "claims"), cancelled, then uploaded file B (e.g.
  // "risk_premiums") would still see the stale "claims" seed in the
  // dropdown — setClassificationResult's seeding logic only populates
  // reportType when it's currently null, which it wouldn't be after run 1.
  // Clearing on run-start is the single source of truth so the dropdown
  // always reseeds from the latest backend detection.
  const setClassifying = useCallback((isClassifying: boolean) => {
    setState((prev) =>
      isClassifying
        ? {
            ...prev,
            isClassifying: true,
            detectedType: null,
            reportType: null,
            allowedReportTypes: [],
          }
        : { ...prev, isClassifying: false }
    );
  }, []);

  const setClassificationResult = useCallback(
    (data: { detectedType: string | null; allowedReportTypes: string[] }) => {
      setState((prev) => ({
        ...prev,
        detectedType: data.detectedType,
        allowedReportTypes: data.allowedReportTypes,
        // Seed reportType from the detection if (and only if) the user
        // hasn't already picked something. "unknown" is never seeded —
        // the dropdown must force a conscious choice in that case.
        reportType:
          prev.reportType !== null
            ? prev.reportType
            : data.detectedType && data.detectedType !== "unknown"
              ? data.detectedType
              : null,
        isClassifying: false,
      }));
    },
    []
  );

  const setReportType = useCallback((reportType: string | null) => {
    setState((prev) => ({ ...prev, reportType }));
  }, []);

  return (
    <UploadContext.Provider
      value={{
        ...state,
        confirmUpload,
        resetUpload,
        saveMappings,
        setDqxRunInfo,
        setClassifying,
        setClassificationResult,
        setReportType,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUpload(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUpload must be used within <UploadProvider>");
  }
  return ctx;
}
